import PptxGenJS from "pptxgenjs";

export const slide6 = (pptx: PptxGenJS, data: any) => {
    const slide = pptx.addSlide();
    const reportData = data?.data;
    const combinedQuestions = reportData?.participantReport?.combinedQuestions || (Array.isArray(reportData) ? reportData : []);

    if (!combinedQuestions || combinedQuestions.length === 0) return;

    // Logo
    slide.addImage({
        path: '/Access-Holdings-Logo.jpg',
        x: 0.5,
        y: 0.2,
        w: 1.3,
        h: 0.45
    });

    // Page Title
    slide.addText("Your Strengths and Development Needs", {
        x: 0.5,
        y: 0.8,
        w: 7.27,
        h: 0.5,
        fontSize: 20,
        bold: true,
        color: '000000'
    });

    // Intro text
    slide.addText([
        { text: "• The behaviors listed below were identified as ", options: { fontSize: 10, color: '404040' } },
        { text: "strengths and development needs.", options: { fontSize: 10, color: '404040', bold: true } }
    ], {
        x: 0.5,
        y: 1.25,
        w: 7.27,
        h: 0.3
    });

    // Helper to add a section with its table
    const addSection = (
        s: any,
        baseTop: number,
        titleMain: string,
        titleHighlight: string,
        subtitle: string,
        rows: { behavior: string, competency: string }[]
    ) => {
        // Section Title
        s.addText([
            { text: titleMain + " ", options: { fontSize: 12, bold: true, color: '404040' } },
            { text: titleHighlight, options: { fontSize: 12, bold: true, color: 'FF6600' } },
            { text: subtitle, options: { fontSize: 9, color: '595959' } }
        ], {
            x: 0.5,
            y: baseTop,
            w: 7.27,
            h: 0.4
        });

        const tableTop = baseTop + 0.4;
        const colW = [4.5, 2.77];
        const headerH = 0.3;
        const rowH = 0.5;

        // Table Header Background
        s.addShape('rect', { x: 0.5, y: tableTop, w: colW[0], h: headerH, fill: { color: 'EAEAEA' }, line: { color: '404040', width: 1 } });
        s.addShape('rect', { x: 0.5 + colW[0], y: tableTop, w: colW[1], h: headerH, fill: { color: 'EAEAEA' }, line: { color: '404040', width: 1 } });

        // Header Text
        s.addText("BEHAVIORS", { x: 0.6, y: tableTop, w: colW[0] - 0.2, h: headerH, fontSize: 10, bold: true, valign: 'middle' });
        s.addText("COMPETENCY (DOMAIN)", { x: 0.5 + colW[0] + 0.1, y: tableTop, w: colW[1] - 0.2, h: headerH, fontSize: 10, bold: true, valign: 'middle' });

        // Rows
        rows.forEach((r, i) => {
            const y = tableTop + headerH + (i * rowH);
            s.addShape('rect', { x: 0.5, y: y, w: colW[0], h: rowH, line: { color: '404040', width: 1 } });
            s.addShape('rect', { x: 0.5 + colW[0], y: y, w: colW[1], h: rowH, line: { color: '404040', width: 1 } });

            s.addText(r.behavior, { x: 0.6, y: y, w: colW[0] - 0.2, h: rowH, fontSize: 9, color: '404040', valign: 'middle' });
            s.addText(r.competency, { x: 0.5 + colW[0] + 0.1, y: y, w: colW[1] - 0.2, h: rowH, fontSize: 9, color: '404040', valign: 'middle' });
        });

        return tableTop + headerH + (rows.length * rowH);
    };

    // Filter out comments and invalid scores
    const behaviors = combinedQuestions.filter((q: any) => q.tag !== "Comments" && q.my_score !== null);


    const processedBehaviors = behaviors.map((q: any) => {
        let totalScore = 0;
        let totalRaters = 0;
        q.raters.forEach((r: any) => {
            totalScore += r.avg_score * r.rater_count;
            totalRaters += r.rater_count;
        });
        const avgRaterScore = totalRaters > 0 ? totalScore / totalRaters : 0;
        const gap = avgRaterScore - q.my_score;
        const averageComposite = (q.my_score + avgRaterScore) / 2;

        return {
            behavior: q.text,
            competency: q.tag,
            my_score: q.my_score,
            avg_rater_score: avgRaterScore,
            gap: gap,
            averageComposite: averageComposite
        };
    });

    // 1. CONSENSUS STRENGTHS: Top 3 by averageComposite
    const consensusStrengthsData = [...processedBehaviors]
        .sort((a, b) => b.averageComposite - a.averageComposite)
        .slice(0, 3)
        .map(b => ({ behavior: b.behavior, competency: b.competency }));

    // 2. HIDDEN STRENGTHS: Top 3 by positive gap (Raters > Self)
    const hiddenStrengthsData = [...processedBehaviors]
        .filter(b => b.gap > 0)
        .sort((a, b) => b.gap - a.gap)
        .slice(0, 3)
        .map(b => ({ behavior: b.behavior, competency: b.competency }));

    // 3. CONSENSUS DEVELOPMENT NEEDS: Bottom 3 by averageComposite
    const devNeedsData = [...processedBehaviors]
        .sort((a, b) => a.averageComposite - b.averageComposite)
        .slice(0, 3)
        .map(b => ({ behavior: b.behavior, competency: b.competency }));

    const topAfter1 = addSection(slide, 1.6, "CONSENSUS", "STRENGTHS", " (These behaviors were rated HIGHEST by your raters and yourself.)", consensusStrengthsData);

    const topAfter2 = addSection(slide, topAfter1 + 0.4, "HIDDEN", "STRENGTHS", "\n(These behaviors had the greatest POSITIVE gap between your raters' score and your score.)", hiddenStrengthsData);

    addSection(slide, topAfter2 + 0.4, "CONSENSUS", "DEVELOPMENT NEEDS", "\n(These behaviors were rated LOWEST by your raters and yourself.)", devNeedsData);

    // Footer
    const candidateName = data?.candidateName || 'Jane Doe';
    slide.addText(`360-Degree Feedback Individual Report for ${candidateName}`, {
        x: 0,
        y: 11.2,
        w: 7.8,
        h: 0.3,
        fontSize: 9,
        color: '666666',
        align: 'center'
    });

    // slide.addText("6", {
    //     x: 7.8,
    //     y: 11.2,
    //     w: 0.4,
    //     h: 0.3,
    //     fontSize: 9,
    //     color: '666666',
    //     align: 'center'
    // });
};
